import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { Maximize2 } from 'lucide-react';

const SLOT_MS = 15 * 60 * 1000;

export default function KioskPage() {
  const [code,       setCode]       = useState('------');
  const [expiresAt,  setExpiresAt]  = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [salonNom,   setSalonNom]   = useState('');
  const [error,      setError]      = useState(false);

  const fetchCode = useCallback(async () => {
    try {
      const [codeRes, salonRes] = await Promise.all([
        api.get('/salon/me/kiosque-code'),
        api.get('/salon/me'),
      ]);
      setCode(codeRes.data.data.code);
      setExpiresAt(codeRes.data.data.expiresAt);
      setSalonNom(salonRes.data.data?.nom || '');
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  // Chargement initial + refresh automatique quand le code expire
  useEffect(() => {
    fetchCode();
  }, [fetchCode]);

  // Compte à rebours + déclenchement du refresh
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(diff);
      if (diff === 0) fetchCode();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, fetchCode]);

  const totalSeconds = SLOT_MS / 1000; // 900
  const progress = secondsLeft !== null ? secondsLeft / totalSeconds : 1;
  const radius = 54;
  const circ   = 2 * Math.PI * radius;
  const dash   = circ * progress;

  const mins = secondsLeft !== null ? String(Math.floor(secondsLeft / 60)).padStart(2, '0') : '--';
  const secs = secondsLeft !== null ? String(secondsLeft % 60).padStart(2, '0') : '--';

  const goFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center select-none relative overflow-hidden">

      {/* Fond décoratif */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-amber-500/5 blur-3xl" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[400px] h-[400px] rounded-full bg-blue-500/5 blur-3xl" />
      </div>

      {/* Bouton plein écran */}
      <button
        onClick={goFullscreen}
        className="absolute top-6 right-6 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors"
        title="Plein écran"
      >
        <Maximize2 size={18} />
      </button>

      <div className="relative z-10 flex flex-col items-center gap-10 px-8">

        {/* Nom du salon */}
        <div className="text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.35em] text-white/25 mb-2">
            Borne de pointage
          </p>
          <h1 className="text-2xl md:text-3xl font-black text-white/70 tracking-tight">
            {salonNom || '…'}
          </h1>
        </div>

        {/* Code principal */}
        <div className="flex flex-col items-center gap-6">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/20">
            Code du moment
          </p>

          {error ? (
            <p className="text-red-400 font-bold text-sm">Erreur de connexion - rechargez la page</p>
          ) : (
            <div className="flex gap-3 md:gap-4">
              {code.split('').map((digit, i) => (
                <div
                  key={i}
                  className="w-14 h-20 md:w-20 md:h-28 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center"
                >
                  <span className="text-4xl md:text-6xl font-black text-white tracking-tight tabular-nums">
                    {digit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Compte à rebours circulaire */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
              {/* Piste */}
              <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
              {/* Progression */}
              <circle
                cx="60" cy="60" r={radius}
                fill="none"
                stroke={secondsLeft !== null && secondsLeft < 60 ? '#f59e0b' : '#3b82f6'}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circ}`}
                style={{ transition: 'stroke-dasharray 1s linear, stroke 0.5s' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-black text-white tabular-nums">{mins}:{secs}</span>
              <span className="text-[9px] font-bold text-white/25 uppercase tracking-widest mt-0.5">restant</span>
            </div>
          </div>
          <p className="text-xs text-white/20 font-medium">
            {secondsLeft !== null && secondsLeft < 60
              ? 'Nouveau code imminent…'
              : 'Le code change toutes les 15 min'}
          </p>
        </div>

        {/* Instruction */}
        <div className="mt-2 px-6 py-4 bg-white/5 border border-white/8 rounded-2xl text-center max-w-xs">
          <p className="text-white/40 text-sm font-medium leading-relaxed">
            Saisissez ce code dans l'application pour pointer votre arrivée.
          </p>
        </div>

      </div>
    </div>
  );
}
